import {
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    Link,
    Modal,
    TextField,
    Tooltip,
    Typography,
    useTheme,
    capitalize,
} from "@mui/material";
import React, { useState } from "react";
import * as Yup from "yup";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams } from "react-router-dom";
import queryGraphql from "../../../helpers/ApiInterface";
import { useAuth } from "../../../helpers/AuthProvider";
import { Close } from "@mui/icons-material";
import { Formik, ErrorMessage, Form } from "formik";

import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import MiniBot from "../../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";

// --- Interfaces ---
interface ApplicationInfo {
    applicationName: string;
}

interface SolutionOwnerInfo {
    name: string;
}

interface DataPipelineNestedDetails {
    projectId: string;
    networkProjectId: string;
    region: string;
    environmentSize: string;
    // Fields for approver input
    network?: string;
    subNetwork?: string;
    storageBucket?: string;
    serviceAccount?: string;
    podRange?: string;
    serviceRange?: string;
}

interface DataPipelineDetailsData {
    id: string;
    name: string;
    solutionCategoryType: 'composer' | 'dataproc' | string;
    status: string;
    cloudProvider: 'gcp' | 'aws' | 'azure' | string;
    details: {
        dataPipeline: DataPipelineNestedDetails;
    };
    solutionOwner: SolutionOwnerInfo;
    application?: ApplicationInfo;
    reasonForRejection?: string;
}

// --- Validation Schemas ---
const approvalValidationSchema = (provider?: string) => Yup.object().shape({
    network: Yup.string().required("This field is required"),
    subNetwork: Yup.string().required("This field is required"),
    storageBucket: Yup.string().required("This field is required"),
    serviceAccount: Yup.string().required("This field is required"),
    // GCP-specific validations
    podRange: provider === 'gcp'
        ? Yup.string().required("Pod Range is required for GCP")
        : Yup.string().optional(),
    serviceRange: provider === 'gcp'
        ? Yup.string().required("Service Range is required for GCP")
        : Yup.string().optional(),
});

const rejectValidationSchema = Yup.object().shape({
    reasonForRejection: Yup.string().required("Reason for rejection is required").min(5, "Reason must be at least 5 characters long"),
});

// --- Helper Component ---
const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
    const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}</Typography>
        </Box>
    );
};

// --- Main Content ---
const DataPipelineApproveFormPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [dataPipeline, setDataPipeline] = useState<DataPipelineDetailsData | null>(null);
    const [pipelineEndpoint, setPipelineEndpoint] = useState<string | null>(null);
    const [openRejectModal, setOpenRejectModal] = useState(false);
    const [openApproveModal, setOpenApproveModal] = useState(false);
    const [dynamicLabels, setDynamicLabels] = useState({
        projectId: 'Project ID',
        networkProjectId: 'Network Project ID',
        network: 'Network',
        subNetwork: 'Sub-Network'
    });
    const fallback = '-';

    React.useEffect(() => {
        const fetchData = async () => {
            if (!id) return;
            try {
                const data = await queryGraphql(
                    `
                    query GetDataPipeline($getDataPipelineId: String!) {
                      getDataPipeline(id: $getDataPipelineId) {
                        id, name, solutionCategoryType, status, reasonForRejection, cloudProvider
                        details { 
                          dataPipeline { 
                            projectId, networkProjectId, region, environmentSize,
                            network, subNetwork, storageBucket, serviceAccount, podRange, serviceRange 
                          } 
                        }
                        solutionOwner { id, name }
                        application { id, applicationName }
                      }
                    }
                    `, { getDataPipelineId: id }
                );
                if (data.getDataPipeline) {
                    setDataPipeline(data.getDataPipeline);
                } else {
                    console.log("Data Pipeline details not found.");
                    setDataPipeline(null);
                }
            } catch (error) {
                console.log("Error fetching Data Pipeline details:", error);
            }
        };
        fetchData();
    }, [id]);

    React.useEffect(() => {
        if (dataPipeline?.cloudProvider) {
            switch (dataPipeline.cloudProvider) {
                case 'aws':
                    setDynamicLabels({
                        projectId: 'Account ID',
                        networkProjectId: 'VPC ID',
                        network: 'VPC',
                        subNetwork: 'Subnet'
                    });
                    break;
                case 'azure':
                    setDynamicLabels({
                        projectId: 'Subscription ID',
                        networkProjectId: 'Resource Group',
                        network: 'Virtual Network (VNet)',
                        subNetwork: 'Subnet'
                    });
                    break;
                case 'gcp':
                default:
                    setDynamicLabels({
                        projectId: 'Project ID',
                        networkProjectId: 'Network Project ID',
                        network: 'Network',
                        subNetwork: 'Sub-Network'
                    });
                    break;
            }
        }
    }, [dataPipeline]);


    React.useEffect(() => {
        const fetchEndpointUrl = async () => {
            if (!dataPipeline?.id) return;
            try {
                const data = await queryGraphql(
                    `query ResourceStatus($resourceStatusId: String!) {
                      resourceStatus(id: $resourceStatusId) { id, solutionEndpointUrl }
                    }`, { resourceStatusId: dataPipeline.id }
                );
                if (data.resourceStatus?.solutionEndpointUrl) {
                    setPipelineEndpoint(data.resourceStatus.solutionEndpointUrl);
                }
            } catch (error) {
                console.log("Error fetching pipeline endpoint URL:", error);
            }
        };
        fetchEndpointUrl();
    }, [dataPipeline]);

    const statusDisplay = dataPipeline?.status ? capitalize(dataPipeline.status) : "Status Unknown";
    const statusBgColor = dataPipeline?.status === "active" || dataPipeline?.status === "approved" ? "#12B76A10" : dataPipeline?.status === "rejected" ? "#F0443810" : "#FDB02210";
    const statusIcon = dataPipeline?.status === "active" || dataPipeline?.status === "approved" ? "/assets/tickcircle.svg" : dataPipeline?.status === "rejected" ? "/assets/reject.svg" : "/assets/clock.svg";

    return (
        <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/datasolutions/requests/data-pipelines" onClick={(e) => { e.preventDefault(); navigate("/datasolutions/requests/data-pipelines") }}>
                        Data Pipeline Requests
                    </Link>
                    <Typography color="text.primary">Pipeline Details</Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={1}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/datasolutions/requests/data-pipelines")} >
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">Pipeline Details</Typography>
                </Box>
            </Box>
            <Grid container justifyContent="center">
                <Grid item xs={11} md={11} lg={11}>
                    {dataPipeline && (
                        <Formik
                            enableReinitialize // Important for pre-populating form
                            initialValues={{
                                network: dataPipeline.details.dataPipeline.network || "",
                                subNetwork: dataPipeline.details.dataPipeline.subNetwork || "",
                                storageBucket: dataPipeline.details.dataPipeline.storageBucket || "",
                                serviceAccount: dataPipeline.details.dataPipeline.serviceAccount || "",
                                podRange: dataPipeline.details.dataPipeline.podRange || "",
                                serviceRange: dataPipeline.details.dataPipeline.serviceRange || "",
                            }}
                            validationSchema={approvalValidationSchema(dataPipeline.cloudProvider)}
                            onSubmit={async (values) => {
                                const detailsPayload: any = {
                                    network: values.network,
                                    subNetwork: values.subNetwork,
                                    storageBucket: values.storageBucket,
                                    serviceAccount: values.serviceAccount,
                                };

                                if (dataPipeline.cloudProvider === 'gcp') {
                                    detailsPayload.podRange = values.podRange;
                                    detailsPayload.serviceRange = values.serviceRange;
                                }

                                try {
                                    const approvalData = await queryGraphql(
                                        `mutation ApproveSolutionDataPipeline($input: ApproveSolutionInput!) { approveSolutionDataPipeline(input: $input) { id name status } }`,
                                        { input: { id: dataPipeline.id, details: { dataPipeline: detailsPayload } } }
                                    );
                                    if (approvalData.approveSolutionDataPipeline?.id) {
                                        setOpenApproveModal(false);
                                        navigate("/datasolutions/requests/data-pipelines");
                                    } else {
                                        console.error("Approval failed", approvalData);
                                    }
                                } catch (error) {
                                    console.error("Error during approval:", error);
                                }
                            }}
                        >
                            {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                                <Form id="approve-pipeline-main-form">
                                    <Box my={2}>
                                        <Box bgcolor={statusBgColor} borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
                                                <Box sx={{ mb: { xs: 1, sm: 0 } }}>
                                                    <Typography variant="h5" fontFamily="Gilroy-Bold" gutterBottom>{dataPipeline.name || fallback}</Typography>
                                                    <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{`Resource Owner: ${dataPipeline.solutionOwner?.name || fallback}`}</Typography>
                                                </Box>
                                                <Box textAlign="right">
                                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: 'flex-end' }}>
                                                        <img src={statusIcon} alt="Status Icon" style={{ width: "20px", marginRight: "8px" }} />
                                                        <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" sx={{ textTransform: 'capitalize' }}>{statusDisplay}</Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                        
                                        <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
                                            <Grid container spacing={2.5}>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label={dynamicLabels.projectId} value={dataPipeline.details.dataPipeline.projectId} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label={dynamicLabels.networkProjectId} value={dataPipeline.details.dataPipeline.networkProjectId} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Region" value={capitalize(dataPipeline.details.dataPipeline.region)} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Cloud Provider" value={dataPipeline.cloudProvider?.toUpperCase()} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Application Name" value={dataPipeline.application?.applicationName} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Category Type" value={capitalize(dataPipeline.solutionCategoryType)} /></Grid>
                                                {dataPipeline.solutionCategoryType === 'composer' && (<Grid item xs={12} sm={6} md={3}><DetailItem label="Environment Size" value={capitalize(dataPipeline.details.dataPipeline.environmentSize)} /></Grid>)}
                                                {pipelineEndpoint && (<Grid item xs={12} sm={6} md={3}><Typography variant="caption" display="block" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '12px', color: '#666666' }}>Pipeline Endpoint</Typography><Tooltip title={<Link href={pipelineEndpoint} target="_blank" rel="noopener noreferrer" sx={{ color: '#ffffff' }}>{pipelineEndpoint}</Link>}><Avatar src="/assets/info.svg" alt="info" sx={{ height: 16, width: 16, cursor: 'pointer' }} /></Tooltip></Grid>)}
                                            </Grid>
                                        </Box>

                                        {dataPipeline.status === 'pending' && (
                                            <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Cloud Infra Details</Typography>
                                                <Divider sx={{ marginY: 2 }} />
                                                <Grid container spacing={2.5}>
                                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName={dynamicLabels.network} /><TextField name="network" value={values.network} onChange={handleChange} onBlur={handleBlur} placeholder={`Enter ${dynamicLabels.network}`} variant="filled" fullWidth hiddenLabel error={touched.network && Boolean(errors.network)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="network" /></div></Grid>
                                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName={dynamicLabels.subNetwork} /><TextField name="subNetwork" value={values.subNetwork} onChange={handleChange} onBlur={handleBlur} placeholder={`Enter ${dynamicLabels.subNetwork}`} variant="filled" fullWidth hiddenLabel error={touched.subNetwork && Boolean(errors.subNetwork)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="subNetwork" /></div></Grid>
                                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Storage Bucket" /><TextField name="storageBucket" value={values.storageBucket} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Storage Bucket" variant="filled" fullWidth hiddenLabel error={touched.storageBucket && Boolean(errors.storageBucket)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="storageBucket" /></div></Grid>
                                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Service Account" /><TextField name="serviceAccount" value={values.serviceAccount} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Service Account" variant="filled" fullWidth hiddenLabel error={touched.serviceAccount && Boolean(errors.serviceAccount)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="serviceAccount" /></div></Grid>
                                                    
                                                    {dataPipeline.cloudProvider === 'gcp' && (
                                                        <>
                                                            <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Pod IP Address Range" /><TextField name="podRange" value={values.podRange} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Pod Range" variant="filled" fullWidth hiddenLabel error={touched.podRange && Boolean(errors.podRange)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="podRange" /></div></Grid>
                                                            <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Service IP Address Range" /><TextField name="serviceRange" value={values.serviceRange} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Service Range" variant="filled" fullWidth hiddenLabel error={touched.serviceRange && Boolean(errors.serviceRange)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="serviceRange" /></div></Grid>
                                                        </>
                                                    )}
                                                </Grid>
                                            </Box>
                                        )}
                                        
                                        {dataPipeline.status === 'active' && (
                                            <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Cloud Infra Details</Typography>
                                                <Divider sx={{ marginY: 2 }} />
                                                <Grid container spacing={2.5}>
                                                    <Grid item xs={12} sm={6} md={3}><DetailItem label={dynamicLabels.network} value={dataPipeline.details.dataPipeline.network} /></Grid>
                                                    <Grid item xs={12} sm={6} md={3}><DetailItem label={dynamicLabels.subNetwork} value={dataPipeline.details.dataPipeline.subNetwork} /></Grid>
                                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="Storage Bucket" value={dataPipeline.details.dataPipeline.storageBucket} /></Grid>
                                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="Service Account" value={dataPipeline.details.dataPipeline.serviceAccount} /></Grid>
                                                    
                                                    {dataPipeline.cloudProvider === 'gcp' && (
                                                        <>
                                                            <Grid item xs={12} sm={6} md={3}><DetailItem label="Pod IP Address Range" value={dataPipeline.details.dataPipeline.podRange} /></Grid>
                                                            <Grid item xs={12} sm={6} md={3}><DetailItem label="Service IP Address Range" value={dataPipeline.details.dataPipeline.serviceRange} /></Grid>
                                                        </>
                                                    )}
                                                </Grid>
                                            </Box>
                                        )}


                                        {dataPipeline.status === 'rejected' && (
                                             <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                                                <Divider sx={{ marginY: 2 }} />
                                                <DetailItem label="Reason for Rejection" value={dataPipeline.reasonForRejection} />
                                             </Box>
                                        )}

                                    </Box>
                                    
                                    {dataPipeline?.status === "pending" && user.roles.some((role: string) => ["infrastructure_admin"].includes(role)) && (
                                        <Box display="flex" justifyContent="flex-end" gap={2} mt={3} mb={3}>
                                            <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold" }} variant="outlined" size="large" onClick={() => setOpenRejectModal(true)}>Reject Request</Button>
                                            <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "rgba(0,0,0,0.85)"}, textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' } }} variant="contained" size="large" disabled={!isValid || !dirty} onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
                                        </Box>
                                    )}
                                </Form>
                            )}
                        </Formik>
                    )}
                </Grid>
            </Grid>

            {/* Simple Confirmation Modal for Approval */}
            <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 500 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                        <Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Approve Request</Typography>
                        <IconButton aria-label="close" onClick={() => setOpenApproveModal(false)}><Close fontSize="small" /></IconButton>
                    </Box>
                    <Divider />
                    <Typography p={3}>Are you sure you want to approve this Data Pipeline request?</Typography>
                    <Divider />
                    <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                        <Button variant="outlined" onClick={() => setOpenApproveModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                        <Button type="submit" form="approve-pipeline-main-form" sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" } }} variant="contained">Approve</Button>
                    </Box>
                </Box>
            </Modal>

            {/* Rejection Modal with its own Formik instance */}
            <Modal open={openRejectModal} onClose={() => setOpenRejectModal(false)}>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 700 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px", display: 'flex', flexDirection: 'column' }}>
                    <Formik
                        initialValues={{ reasonForRejection: "" }}
                        validationSchema={rejectValidationSchema}
                        onSubmit={async ({ reasonForRejection }) => {
                            if (!id) return;
                            try {
                                const rejectionData = await queryGraphql(
                                    `mutation RejectSolutionDataPipeline($input: RejectSolutionInput!) { rejectSolutionDataPipeline(input: $input) { id name } }`,
                                    { input: { id: id, reasonForRejection } }
                                );
                                if (rejectionData.rejectSolutionDataPipeline?.id) {
                                    setOpenRejectModal(false);
                                    navigate("/datasolutions/requests/data-pipelines");
                                }
                            } catch (error) { console.error("Error during rejection:", error); }
                        }}
                    >
                        {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                            <Form id="reject-pipeline-form" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                                    <Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject Data Pipeline Request</Typography>
                                    <IconButton aria-label="close" onClick={() => setOpenRejectModal(false)}><Close fontSize="small" /></IconButton>
                                </Box>
                                <Divider />
                                <Box sx={{ p: 3, flexGrow: 1 }}>
                                    <CompulsoryFieldsLabel fieldName="Comments" />
                                    <TextField variant="filled" fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
                                    <Box sx={{ color: "red", mt: 1 }}><ErrorMessage name="reasonForRejection" /></Box>
                                </Box>
                                <Divider />
                                <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                                    <Button variant="outlined" onClick={() => setOpenRejectModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                                    <Button type="submit" form="reject-pipeline-form" disabled={!isValid || !dirty} sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' } }} variant="contained">Reject</Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </Modal>
        </Container>
    );
};

const DataPipelineApproveFormPage: React.FC = () => {
    const theme = useTheme();
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(() => typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('isFaradayDrawerOpen') || 'true') : true);
    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
    };
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"requests-data-pipelines"} onStateChange={handleDrawerStateChange} />
            <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), marginLeft: { xs: 0, sm: `${currentMarginLeft}px` } }}>
                <DataPipelineApproveFormPageContent />
            </Box>
        </Box>
    );
};

export default DataPipelineApproveFormPage;